import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const INACTIVITY_LIMIT_MS = 30 * 60 * 1000; // 30 minutes

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) { setLoading(false); return; }
    // Session ends when the browser is closed: sessionStorage survives refresh & navigation only
    if (!sessionStorage.getItem("session_open")) {
      localStorage.removeItem("token");
      setLoading(false);
      return;
    }
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => localStorage.removeItem("token"))
      .finally(() => setLoading(false));
  }, []);

  const logout = useCallback((reason) => {
    localStorage.removeItem("token");
    sessionStorage.removeItem("session_open");
    setUser(null);
    if (reason === "inactivity") localStorage.setItem("logout_reason", "inactivity");
    window.location.href = "/login";
  }, []);

  // Auto-logout after a period of inactivity (only while logged in)
  useEffect(() => {
    if (!user) return;
    const reset = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => logout("inactivity"), INACTIVITY_LIMIT_MS);
    };
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [user, logout]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("token", data.token);
    sessionStorage.setItem("session_open", "1");
    const me = await api.get("/auth/me");
    setUser(me.data);
    return me.data;
  };

  const refresh = async () => {
    const me = await api.get("/auth/me");
    setUser(me.data);
    return me.data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}
